import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useUpdateEnergyAsset } from "../../../hooks";

interface UploadReadingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetInfo: any;
  assetType: "generator" | "renewable" | "electric-utility";
  onSubmit: (data: any) => Promise<void> | void;
  loading?: boolean;
  formFields?: FormField[];
  customTitle?: string;
  customDescription?: string;
}

export const UploadReadingModal = ({
  open,
  onOpenChange,
  assetInfo,
  assetType,
  onSubmit,
  loading = false,
  formFields,
  customTitle,
  customDescription,
}: UploadReadingModalProps) => {
  const updateEnergyAsset = useUpdateEnergyAsset(assetInfo?.id, assetType);
  const getTitle = () => {
    if (customTitle) return customTitle;

    const typeMap = {
      generator: "Generator",
      renewable: "Renewable Energy",
      "electric-utility": "Electric Utility",
    };
    return `Upload ${typeMap[assetType]} Reading`;
  };

  const getDescription = () => {
    if (customDescription) return customDescription;

    const typeMap = {
      generator: "generator",
      renewable: "renewable energy",
      "electric-utility": "electric utility",
    };
    return `Enter energy reading data for this ${typeMap[assetType]} asset`;
  };

  const defaultFormFields: FormField[] = useMemo(() => {
    const baseFields: FormField[] = [
      {
        name: "previousReading",
        label: "Previous Reading",
        type: "number",
        placeholder: "Enter previous reading",
        disabled: true,
        min: 0,
        step: 0.01,
      },
      {
        name: "previousDate",
        label: "Previous Reading Date",
        type: "datepicker",
        placeholder: "Pick date",
        disabled: true,
      },
      {
        name: "currentReading",
        label: "Current Reading",
        type: "number",
        required: true,
        placeholder: "Enter current reading",
        min: assetInfo.previousReading ?? 0,
        step: 0.01,
      },
      {
        name: "currentDate",
        label: "Current Reading Date",
        type: "datepicker",
        required: true,
        placeholder: "Pick date",
      },
      {
        name: "consumption",
        label: "Consumption",
        type: "text",
        required: false,
        placeholder: "Auto-calculated",
        disabled: true,
        helperText: "Auto-calculated from readings",
      },
    ];

    return baseFields;
  }, [assetType]);

  const fields = defaultFormFields;

  // Create form with populateForm
  const form: UseFormReturn<any> = FormService.createForm({
    fields,
    mode: "onChange",
  });

  // Populate form with asset info when component mounts or assetInfo changes
  useEffect(() => {
    if (assetInfo) {
      FormService.populateForm(
        form,
        {
          previousReading: assetInfo.currentReading,
          previousDate: assetInfo.currentReadingDate,
        },
        defaultFormFields
      );
    }
  }, [assetInfo, form, assetType]);

  const assetData = [
    { label: "Asset Id", value: assetInfo?.asset?.assetNo },
    { label: "Asset Name", value: assetInfo?.asset?.assetName },
    assetType === "electric-utility" && {
      label: "Device Number",
      value: assetInfo.deviceNumber ?? "NA",
    },
    {
      label: "System / Network",
      value:
        assetInfo?.asset?.systemName ?? assetInfo?.asset?.networkName ?? "NA",
    },
    { label: "Units", value: assetInfo.units },
  ];

  // Watch current reading to calculate consumption
  const currentReading = form.watch("currentReading");

  useEffect(() => {
    if (currentReading && assetInfo.previousReading) {
      const consumption =
        Number(currentReading) - Number(assetInfo.previousReading);
      form.setValue("consumption", consumption.toFixed(2), {
        shouldValidate: false,
      });
    } else {
      form.setValue("consumption", "", { shouldValidate: false });
    }
  }, [currentReading, assetInfo.previousReading, form]);

  // Handle form submission
  const handleSubmit = async (data: any) => {
    const payload = {
      current_reading: data.currentReading,
      current_reading_date: data.currentDate,
    };

    await updateEnergyAsset.mutateAsync(payload);
    onSubmit(payload);
    form.reset();
    onOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
      <Button type="button" variant="outline" onClick={handleCancel}>
        Cancel
      </Button>
      <Button
        type="submit"
        disabled={loading}
        isLoading={updateEnergyAsset.isPending}
      >
        Submit Reading
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        {/* Header */}
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold">
                {getTitle()}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600 mt-1">
                {getDescription()}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Asset Information Card */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            {assetData
              .filter(Boolean) // Remove falsy values (like the conditional electric-utility entry)
              .map((item, index) => (
                <div key={index}>
                  <div className="text-xs font-medium text-gray-500 mb-1">
                    {item.label}:
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {item.value}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Dynamic Form */}
        <DynamicForm
          fields={fields}
          form={form}
          onSubmit={handleSubmit}
          loading={loading}
          gridColumns={2}
          responsiveColumns={{
            sm: 1,
            md: 2,
          }}
          config={{
            showProgress: false,
            layout: "vertical",
            spacing: "none",
            showResetButton: false,
            resetOnSubmit: false,
            showSubmitButton: false,
            classes: FIELD_CLASSES,
          }}
          renderCustomFooter={renderCustomFooter}
        />
      </DialogContent>
    </Dialog>
  );
};
